import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { ScheduleCounterSignRobosignSessionResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Calendar data from Calendly event
 */
export interface CalendlyCalendarData {
	eventId: string;
	inviteeId: string;
}

/**
 * Input type for scheduling a counter-sign robosign session
 */
export interface ScheduleCounterSignRobosignSessionInput {
	calendarData: CalendlyCalendarData;
}

/**
 * API function to schedule a counter-signing robotic session.
 * Creates session in ServicesProviders/{svpId}/ScheduledSessions (late-joiner pattern).
 * Multiple orders can share a single scheduled session for the same services provider.
 */
export const scheduleCounterSignRobosignSession = async (
	servicesProviderId: string,
	orderId: string,
	calendarData: CalendlyCalendarData,
): Promise<ScheduleCounterSignRobosignSessionResponse> => {
	const response = await apiClient.post<ScheduleCounterSignRobosignSessionResponse>(
		ordersEndpoints.scheduleCounterSignRobosignSession(servicesProviderId, orderId),
		{ calendarData },
	);
	return response.data;
};

/**
 * Hook for scheduling a counter-signing robotic session.
 * Uses late-joiner pattern where sessions are stored at the SVP level,
 * allowing multiple orders to share a single scheduled session.
 *
 * @param orderId - The purchased vehicle ID
 */
export const useScheduleCounterSignRobosignSession = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: ScheduleCounterSignRobosignSessionInput) => {
			if (!orderId) {
				throw new Error('Unable to schedule counter-signing session without an order ID.');
			}
			if (!servicesProviderId) {
				throw new Error(
					'Unable to schedule counter-signing session without a services provider ID.',
				);
			}
			return scheduleCounterSignRobosignSession(
				servicesProviderId,
				orderId,
				input.calendarData,
			);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.SCHEDULE_COUNTER_SIGN_ROBOSIGN_SESSION);
		},
	});
};
